import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { todayIso } from '../utils/dateUtils';

// POS Reports → Bill to Company. Bills settled to a company's account (Corporate
// Master), what each company owes, and recording the company's payment, which
// is adjusted against its oldest unpaid bills first.

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '-';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '-' : x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const monthStart = () => `${todayIso().slice(0, 8)}01`;
const statusOf = (b) => (b.received >= b.amount - 0.004 ? 'PAID' : b.received > 0 ? 'PART' : 'UNPAID');
const STATUS = {
  PAID: { label: 'Paid', bg: '#dcfce7', fg: '#166534' },
  PART: { label: 'Part paid', bg: '#fef3c7', fg: '#92400e' },
  UNPAID: { label: 'Unpaid', bg: '#fee2e2', fg: '#991b1b' },
};
const PAY_MODES = [['BANK_TRANSFER', 'Bank Transfer'], ['NEFT', 'NEFT / RTGS'], ['CHEQUE', 'Cheque'], ['UPI', 'UPI'], ['CARD', 'Card'], ['CASH', 'Cash']];

function csvCell(v) {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function KotBillToCompany({ headers, isMobile = false, canRecordPayment = true }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayIso());
  const [outletScope, setOutletScope] = useState('all');
  const [status, setStatus] = useState('');
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selCo, setSelCo] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'BANK_TRANSFER', reference_no: '', remarks: '' });
  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState('');
  const [toast, setToast] = useState('');
  // Bills ticked in the bill list (ids as strings). Paying "selected" pays only these.
  const [picked, setPicked] = useState(() => new Set());
  const [payPicked, setPayPicked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams({ from, to, outlet: outletScope === 'current' ? 'current' : 'all' });
      const res = await fetch(`/api/v1/kot/reports/bill-to-company?${q}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load Bill to Company report');
      setBills(Array.isArray(data.bills) ? data.bills : []);
      setPayments(Array.isArray(data.payments) ? data.payments : []);
    } catch (err) {
      setError(err?.message || 'Failed to load Bill to Company report');
    } finally {
      setLoading(false);
    }
  }, [headers, from, to, outletScope]);

  useEffect(() => { load(); }, [load]);
  // A new filter window starts with nothing ticked.
  useEffect(() => { setPicked(new Set()); }, [from, to, outletScope, status]);

  const filtered = useMemo(() => bills.filter((b) => !status || statusOf(b) === status), [bills, status]);
  const companies = useMemo(() => {
    const map = new Map();
    filtered.forEach((b) => {
      const c = map.get(b.corporate_id) || {
        id: b.corporate_id, name: b.corporate_name, code: b.corporate_code, gst: b.gst_number,
        limit: Number(b.credit_limit || 0), n: 0, amount: 0, received: 0, oldest: null,
      };
      c.n += 1;
      c.amount += Number(b.amount || 0);
      c.received += Number(b.received || 0);
      if (statusOf(b) !== 'PAID') {
        const age = Math.max(0, Math.floor((Date.now() - new Date(b.settled_at).getTime()) / 86400000));
        c.oldest = c.oldest == null ? age : Math.max(c.oldest, age);
      }
      map.set(b.corporate_id, c);
    });
    return [...map.values()].sort((a, b) => (b.amount - b.received) - (a.amount - a.received));
  }, [filtered]);
  const totals = useMemo(() => filtered.reduce((t, b) => ({ amount: t.amount + Number(b.amount || 0), received: t.received + Number(b.received || 0) }), { amount: 0, received: 0 }), [filtered]);
  const sel = companies.find((c) => c.id === selCo) || null;
  const list = filtered.filter((b) => !selCo || b.corporate_id === selCo);
  const listTot = list.reduce((t, b) => ({ amount: t.amount + Number(b.amount || 0), received: t.received + Number(b.received || 0) }), { amount: 0, received: 0 });
  // A payment clears dues across all dates, so the due shown in the payment form is all-time for the company.
  const coDueAllTime = useMemo(() => (selCo ? bills.filter((b) => b.corporate_id === selCo) : []).reduce((s, b) => s + Number(b.amount || 0) - Number(b.received || 0), 0), [bills, selCo]);

  // Ticked bills that are still in view.
  const pickedRows = list.filter((b) => picked.has(String(b.id)));
  const pickedDue = pickedRows.reduce((s, b) => s + Number(b.amount || 0) - Number(b.received || 0), 0);
  const pickedCos = [...new Set(pickedRows.map((b) => b.corporate_id))];
  const allPicked = list.length > 0 && list.every((b) => picked.has(String(b.id)));
  const somePicked = pickedRows.length > 0 && !allPicked;
  const togglePick = (id) => setPicked((prev) => {
    const next = new Set(prev);
    const k = String(id);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(list.map((b) => String(b.id))));
  const selectCompany = (id) => { setSelCo(id === selCo ? null : id); setPayOpen(false); setPicked(new Set()); };

  const openPay = () => {
    if (!sel) { setPayMsg('Tick a company in the table first.'); setPayPicked(false); setPayOpen(true); return; }
    setPayMsg('');
    setPayPicked(false);
    setPayForm({ amount: (sel.amount - sel.received).toFixed(2), payment_mode: 'BANK_TRANSFER', reference_no: '', remarks: '' });
    setPayOpen(true);
  };
  const openPayPicked = () => {
    if (pickedCos.length !== 1) return;
    if (selCo !== pickedCos[0]) setSelCo(pickedCos[0]);
    setPayMsg('');
    setPayPicked(true);
    setPayForm({ amount: pickedDue.toFixed(2), payment_mode: 'BANK_TRANSFER', reference_no: '', remarks: '' });
    setPayOpen(true);
  };
  const savePay = async () => {
    if (!sel) return;
    const amount = Math.round(Number(payForm.amount || 0) * 100) / 100;
    if (!(amount > 0)) { setPayMsg('Enter the amount received'); return; }
    if (payForm.payment_mode !== 'CASH' && !payForm.reference_no.trim()) { setPayMsg('Enter the UTR / cheque / reference number'); return; }
    setPayBusy(true);
    setPayMsg('');
    try {
      const res = await fetch(`/api/v1/kot/companies/${sel.id}/payments`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payForm, amount, ...(payPicked ? { bill_ids: pickedRows.filter((b) => b.corporate_id === sel.id).map((b) => String(b.id)) } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to record payment');
      setPayOpen(false);
      setPicked(new Set());
      setToast(`${inr(amount)} from ${sel.name} recorded against ${data.allocated?.length || 0} bill(s). Still due: ${inr(data.remaining_due)}`);
      window.setTimeout(() => setToast(''), 4000);
      await load();
    } catch (err) {
      setPayMsg(err?.message || 'Failed to record payment');
    } finally {
      setPayBusy(false);
    }
  };

  const exportCsv = (source = list) => {
    const head = ['Date', 'Bill No', 'Outlet', 'Company', 'GSTIN', 'Guest / Emp', 'Ref / PO', 'Amount', 'Received', 'Balance', 'Status'];
    const rows = source.map((b) => [fmtDate(b.settled_at), b.outlet_code ? `${b.outlet_code}-${b.bill_no}` : b.bill_no, b.outlet_name || '', b.corporate_name, b.gst_number || '',
      b.guest_name || '', b.reference_no || '', Number(b.amount).toFixed(2), Number(b.received).toFixed(2), (b.amount - b.received).toFixed(2), STATUS[statusOf(b)].label]);
    const csv = [head, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `bill_to_company_${sel ? sel.code || sel.id : 'all'}_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const card = { background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: '1rem', marginBottom: '1rem' };
  const input = { padding: '0.5rem 0.6rem', border: '1px solid var(--theme-border, #cbd5e0)', borderRadius: 8, fontSize: '0.88rem', background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #1f2937)', width: '100%', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--theme-text-muted, #475569)', marginBottom: 3 };
  const btn = (primary) => ({ padding: '0.45rem 0.85rem', borderRadius: 8, border: `1px solid ${primary ? '#1e3a8a' : 'var(--theme-border, #cbd5e0)'}`, background: primary ? '#1e3a8a' : 'transparent', color: primary ? '#fff' : 'var(--theme-text, #1f2937)', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' });
  // Solid navy header with white text: readable on every theme (the app theme
  // paints header text white, which vanished on the old light background).
  const th = {
    padding: '0.6rem 0.7rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em',
    textTransform: 'uppercase', background: '#1e3a8a', color: '#ffffff', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 1, borderBottom: '2px solid #172e6e',
  };
  const td = { padding: '0.55rem 0.7rem', borderTop: '1px solid var(--theme-border, #e5e7eb)', fontSize: '0.84rem', whiteSpace: 'nowrap' };
  const chk = { width: 40, textAlign: 'center' };
  const box = { width: 16, height: 16, cursor: 'pointer', accentColor: '#1e3a8a' };
  const rowBg = (on, i) => (on ? '#e0e7ff' : i % 2 ? 'var(--theme-surface-2, #f8fafc)' : undefined);
  const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div>
      {toast && <div role="status" style={{ ...card, background: '#dcfce7', borderColor: '#86efac', color: '#166534', fontWeight: 600 }}>{toast}</div>}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.25rem', color: '#1e3a8a' }}>Bill to Company</h3>
        <div style={{ fontSize: '0.84rem', color: 'var(--theme-text-muted, #64748b)', marginBottom: '0.8rem' }}>
          POS bills settled to a company's account. Click a company to see its bills, then record the payment when the company settles.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(140px, 1fr))', gap: '0.6rem', marginBottom: '0.8rem' }}>
          <div><label style={lbl} htmlFor="btc-from">From</label><input id="btc-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={input} /></div>
          <div><label style={lbl} htmlFor="btc-to">To</label><input id="btc-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={input} /></div>
          <div><label style={lbl} htmlFor="btc-outlet">Outlet</label>
            <select id="btc-outlet" value={outletScope} onChange={(e) => setOutletScope(e.target.value)} style={input}>
              <option value="all">All outlets</option><option value="current">This outlet only</option>
            </select></div>
          <div><label style={lbl} htmlFor="btc-status">Status</label>
            <select id="btc-status" value={status} onChange={(e) => setStatus(e.target.value)} style={input}>
              <option value="">All</option><option value="UNPAID">Unpaid</option><option value="PART">Part paid</option><option value="PAID">Paid</option>
            </select></div>
        </div>
        {error && <div role="alert" style={{ color: '#b91c1c', marginBottom: '0.6rem' }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: '0.6rem', marginBottom: '0.9rem' }}>
          {[
            ['Companies', companies.length, null],
            ['Bills', filtered.length, null],
            ['Billed', inr(totals.amount), null],
            ['Received', inr(totals.received), '#166534'],
            ['Outstanding', inr(totals.amount - totals.received), '#b91c1c'],
          ].map(([k, v, c]) => (
            <div key={k} style={{ border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10, padding: '0.55rem 0.7rem' }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, color: 'var(--theme-text-muted, #64748b)' }}>{k}</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: c || 'var(--theme-text, #1f2937)', fontVariantNumeric: 'tabular-nums' }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10, marginBottom: '1rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, ...chk }}><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Select</span></th>
              <th style={th}>Company</th><th style={th}>GSTIN</th><th style={{ ...th, ...num }}>Bills</th><th style={{ ...th, ...num }}>Billed</th>
              <th style={{ ...th, ...num }}>Received</th><th style={{ ...th, ...num }}>Outstanding</th><th style={{ ...th, ...num }}>Oldest due</th><th style={{ ...th, ...num }}>Credit limit</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={9} style={{ ...td, textAlign: 'center' }}>Loading…</td></tr>
                : companies.length === 0 ? <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: 'var(--theme-text-muted, #64748b)' }}>No Bill to Company bills in this period. Settle a bill with "Bill to Company" and it appears here.</td></tr>
                  : companies.map((c, i) => (
                    <tr key={c.id} onClick={() => selectCompany(c.id)}
                      style={{ cursor: 'pointer', background: rowBg(c.id === selCo, i) }}>
                      <td style={{ ...td, ...chk }} onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" style={box} checked={c.id === selCo} onChange={() => selectCompany(c.id)} aria-label={`Select ${c.name}`} />
                      </td>
                      <td style={{ ...td, fontWeight: 700 }}>{c.name}</td>
                      <td style={td}>{c.gst || '—'}</td>
                      <td style={{ ...td, ...num }}>{c.n}</td>
                      <td style={{ ...td, ...num }}>{inr(c.amount)}</td>
                      <td style={{ ...td, ...num }}>{inr(c.received)}</td>
                      <td style={{ ...td, ...num, fontWeight: 700, color: c.amount - c.received > 0.004 ? '#b91c1c' : '#166534' }}>{inr(c.amount - c.received)}</td>
                      <td style={{ ...td, ...num }}>{c.oldest == null ? '—' : `${c.oldest} days`}</td>
                      <td style={{ ...td, ...num }}>{c.limit > 0 ? inr(c.limit) : 'No limit'}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          <b>{sel ? `Bills — ${sel.name}` : 'All bills'}</b>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {sel && <button type="button" style={btn(false)} onClick={() => selectCompany(selCo)}>Show all companies</button>}
            <button type="button" style={btn(false)} onClick={() => exportCsv(list)} disabled={!list.length}>Export CSV</button>
            {canRecordPayment && <button type="button" style={btn(true)} onClick={openPay}>Record company payment</button>}
          </div>
        </div>

        {pickedRows.length > 0 && (
          <div role="status" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', background: '#1e3a8a', color: '#fff', borderRadius: 10, padding: '0.55rem 0.8rem', marginBottom: '0.6rem', fontSize: '0.86rem' }}>
            <span><b>{pickedRows.length}</b> bill{pickedRows.length > 1 ? 's' : ''} selected · Balance <b>{inr(pickedDue)}</b>
              {pickedCos.length > 1 && <span style={{ opacity: 0.85 }}> · {pickedCos.length} companies (pick one company to record a payment)</span>}</span>
            <span style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {canRecordPayment && (
                <button type="button" onClick={openPayPicked} disabled={pickedCos.length !== 1 || pickedDue <= 0.004}
                  style={{ ...btn(false), background: '#fff', color: '#1e3a8a', border: '1px solid #fff', opacity: pickedCos.length !== 1 || pickedDue <= 0.004 ? 0.55 : 1 }}>
                  Record payment for selected
                </button>
              )}
              <button type="button" onClick={() => exportCsv(pickedRows)} style={{ ...btn(false), color: '#fff', borderColor: 'rgba(255,255,255,0.6)' }}>Export selected</button>
              <button type="button" onClick={() => setPicked(new Set())} style={{ ...btn(false), color: '#fff', borderColor: 'rgba(255,255,255,0.6)' }}>Clear</button>
            </span>
          </div>
        )}

        {payOpen && (
          <div style={{ border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 10, padding: '0.8rem', marginBottom: '0.8rem' }}>
            {sel ? (
              <>
                <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: '0.5rem' }}>
                  {payPicked
                    ? `Record payment — ${sel.name}, ${pickedRows.length} selected bill${pickedRows.length > 1 ? 's' : ''} (due ${inr(pickedDue)})`
                    : `Record payment — ${sel.name} (due ${inr(coDueAllTime)})`}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.6rem' }}>
                  <div><label style={lbl} htmlFor="btc-pay-amt">Amount received ₹</label>
                    <input id="btc-pay-amt" type="number" min="0.01" step="0.01" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} style={input} /></div>
                  <div><label style={lbl} htmlFor="btc-pay-mode">Mode</label>
                    <select id="btc-pay-mode" value={payForm.payment_mode} onChange={(e) => setPayForm((f) => ({ ...f, payment_mode: e.target.value }))} style={input}>
                      {PAY_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select></div>
                  <div><label style={lbl} htmlFor="btc-pay-ref">UTR / Cheque / Ref no{payForm.payment_mode !== 'CASH' ? ' *' : ''}</label>
                    <input id="btc-pay-ref" value={payForm.reference_no} maxLength={120} onChange={(e) => setPayForm((f) => ({ ...f, reference_no: e.target.value }))} style={input} /></div>
                </div>
                <div style={{ marginTop: '0.5rem' }}><label style={lbl} htmlFor="btc-pay-rem">Remarks</label>
                  <input id="btc-pay-rem" value={payForm.remarks} maxLength={255} onChange={(e) => setPayForm((f) => ({ ...f, remarks: e.target.value }))} style={input} /></div>
                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-muted, #64748b)', marginTop: '0.4rem' }}>
                  {payPicked ? 'Adjusted against the selected bills only, oldest first.' : 'Adjusted against the oldest unpaid bills first.'}
                </div>
              </>
            ) : null}
            {payMsg && <div role="alert" style={{ color: '#b45309', fontSize: '0.84rem', marginTop: '0.4rem' }}>{payMsg}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.6rem' }}>
              <button type="button" style={btn(false)} onClick={() => setPayOpen(false)} disabled={payBusy}>Cancel</button>
              {sel && <button type="button" style={btn(true)} onClick={savePay} disabled={payBusy}>{payBusy ? 'Saving…' : 'Save payment'}</button>}
            </div>
          </div>
        )}

        <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, ...chk }}>
                <input type="checkbox" style={box} checked={allPicked} disabled={!list.length}
                  ref={(el) => { if (el) el.indeterminate = somePicked; }}
                  onChange={toggleAll} aria-label="Select all bills" />
              </th>
              <th style={th}>Date</th><th style={th}>Bill No</th><th style={th}>Outlet</th><th style={th}>Company</th><th style={th}>Guest / Emp</th><th style={th}>Ref / PO</th>
              <th style={{ ...th, ...num }}>Amount</th><th style={{ ...th, ...num }}>Received</th><th style={{ ...th, ...num }}>Balance</th><th style={th}>Status</th>
            </tr></thead>
            <tbody>
              {list.length === 0 ? <tr><td colSpan={11} style={{ ...td, textAlign: 'center', color: 'var(--theme-text-muted, #64748b)' }}>No bills</td></tr>
                : list.map((b, i) => {
                  const st = STATUS[statusOf(b)];
                  const on = picked.has(String(b.id));
                  return (
                    <tr key={b.id} onClick={() => togglePick(b.id)} style={{ cursor: 'pointer', background: rowBg(on, i) }}>
                      <td style={{ ...td, ...chk }} onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" style={box} checked={on} onChange={() => togglePick(b.id)}
                          aria-label={`Select bill ${b.outlet_code ? `${b.outlet_code}-${b.bill_no}` : b.bill_no}`} />
                      </td>
                      <td style={td}>{fmtDate(b.settled_at)}</td>
                      <td style={td}>{b.outlet_code ? `${b.outlet_code}-${b.bill_no}` : b.bill_no}</td>
                      <td style={td}>{b.outlet_name || '-'}</td>
                      <td style={td}>{b.corporate_name}</td>
                      <td style={td}>{b.guest_name || '-'}</td>
                      <td style={td}>{b.reference_no || '-'}</td>
                      <td style={{ ...td, ...num }}>{inr(b.amount)}</td>
                      <td style={{ ...td, ...num }}>{inr(b.received)}</td>
                      <td style={{ ...td, ...num, fontWeight: 700 }}>{inr(b.amount - b.received)}</td>
                      <td style={td}><span style={{ fontSize: '0.74rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: st.bg, color: st.fg }}>{st.label}</span></td>
                    </tr>
                  );
                })}
            </tbody>
            {list.length > 0 && (
              <tfoot><tr>
                <td colSpan={7} style={{ ...td, fontWeight: 700 }}>Total</td>
                <td style={{ ...td, ...num, fontWeight: 700 }}>{inr(listTot.amount)}</td>
                <td style={{ ...td, ...num, fontWeight: 700 }}>{inr(listTot.received)}</td>
                <td style={{ ...td, ...num, fontWeight: 700 }}>{inr(listTot.amount - listTot.received)}</td>
                <td style={td} />
              </tr></tfoot>
            )}
          </table>
        </div>
      </div>

      {payments.length > 0 && (
        <div style={card}>
          <h4 style={{ margin: '0 0 0.6rem' }}>Recent company payments</h4>
          <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={th}>Company</th><th style={{ ...th, ...num }}>Amount</th><th style={th}>Mode</th><th style={th}>Reference</th><th style={th}>Received by</th></tr></thead>
              <tbody>
                {payments.filter((p) => !selCo || p.corporate_id === selCo).slice(0, 50).map((p) => (
                  <tr key={p.id}>
                    <td style={td}>{fmtDate(p.received_at)}</td>
                    <td style={td}>{p.corporate_name}</td>
                    <td style={{ ...td, ...num }}>{inr(p.amount)}</td>
                    <td style={td}>{String(p.payment_mode || '').replace(/_/g, ' ')}</td>
                    <td style={td}>{p.reference_no || '-'}</td>
                    <td style={td}>{p.received_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
